import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../content/SiteContext';


const ServicesSection = () => {
  const { content, t } = useSite();
  const section = content.services;
  const services = section.items;
  const marqueeServices = [...services, ...services]; // Duplicar para efecto infinito
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Velocidad de animación (píxeles por segundo)
  const animationSpeed = 50;

  const totalWidth = marqueeServices.length * 280; // Calcula el ancho total del contenido duplicado

  useEffect(() => {
    let animationId;
    let lastTime = Date.now();

    const animate = () => {
      if (!isDragging && isAnimating) {
        const currentTime = Date.now();
        const deltaTime = currentTime - lastTime;
        const deltaPosition = (animationSpeed * deltaTime) / 1000;

        setCurrentPosition(prev => {
          let newPosition = prev - deltaPosition;

          // Si llega al final, reiniciar a una posición intermedia para crear el efecto infinito
          if (newPosition <= -totalWidth / 2) {
            newPosition = 0;
          }

          return newPosition;
        });

        lastTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isDragging, isAnimating, totalWidth]);

  const handleDragStart = (event) => {
    setIsDragging(true);
    setIsAnimating(false);
    setStartX(event.pageX || event.touches[0].pageX);
    setScrollLeft(currentPosition);
  };

  const handleDragMove = (event) => {
    if (!isDragging) return;
    const x = event.pageX || event.touches[0].pageX;
    const walk = (x - startX) * 2; // Ajuste de velocidad de desplazamiento
    let newPosition = scrollLeft + walk;

    // Asegurar que el desplazamiento no se salga de los límites del contenido duplicado
    if (newPosition > 0) newPosition = 0;
    if (newPosition < -totalWidth / 2) newPosition = -totalWidth / 2;

    setCurrentPosition(newPosition);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setIsAnimating(true);
  };

  return (
    <section id="servicios" className="py-16 md:py-20 bg-gray-950 text-white overflow-hidden">
      <div className="container mx-auto px-4 text-center mb-8 md:mb-12">
        <motion.h2
          className="section-heading text-white reveal"
          style={{
            fontSize: 'clamp(2rem, 6vw, 2.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif'
          }}
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {t(section.title1)}{' '}
          <span
            className="text-gradient"
            style={{
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              backgroundImage: 'linear-gradient(to right, #a78bfa, #f472b6)'
            }}
          >
            {t(section.title2)}
          </span>
        </motion.h2>
        <motion.p
          className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto px-4"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
        >
          {t(section.subtitle)}
        </motion.p>
      </div>
      {/* Carrusel infinito con movimiento físico real */}
      <div className="w-full overflow-hidden py-4 md:py-8">
        <motion.div
          ref={containerRef}
          className="flex min-w-max px-2 md:px-4 cursor-grab active:cursor-grabbing"
          style={{
            width: 'max-content',
            transform: `translateX(${currentPosition}px)`,
            userSelect: 'none',
            touchAction: 'pan-y',
          }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {marqueeServices.map((service, idx) => (
            <div
              key={idx}
              className="service-card-marquee bg-white/10 backdrop-blur-lg border border-white/10 text-white p-4 md:p-8 rounded-2xl shadow-lg flex flex-col items-center text-center"
              style={{
                flexShrink: 0,
                width: '280px',
                margin: '0 0.5rem',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
                padding: '1rem',
                borderRadius: '1.5rem',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                backdropFilter: 'blur(16px)',
                pointerEvents: 'auto'
              }}
            >
              {service.icon && (
                <img src={service.icon} alt={t(service.title)} className="h-8 w-8 md:h-12 md:w-12 mb-3 md:mb-4 object-contain filter invert" draggable="false" />
              )}
              <h4 className="text-lg md:text-xl font-bold mb-2 md:mb-3" style={{ color: '#c03671' }}>{t(service.title)}</h4>
              <p className="text-gray-300 leading-relaxed text-xs md:text-sm">{t(service.description)}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
